import React, {useCallback, useState} from 'react';

import debounce from 'lodash.debounce';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getAccountsByName} from 'src/openapi/admin/apis/GetAccountsByNameApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import Label from '@pb/kit-component-label';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {SearchAccountResult} from './search-account-result';

import type {ChangeEventHandler} from 'react';
import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {ServerResponse} from 'src/typedef';

type Props = {
    label: string,
    selectedAccount?: AccountShortInfo | undefined,
    onSelect: (account: AccountShortInfo | undefined) => void,
    accountId?: number,
    disabled?: boolean,
    width?: number | string,
    placeholder?: string,
};

const MAX_RESULTS = 30;

export const SearchAccount = (props: Props) => {
    const {selectedAccount, label, onSelect, accountId, disabled, width = 315, placeholder} = props;
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [query, setQuery] = useState(selectedAccount?.accountName || '');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<Array<AccountShortInfo>> | null>(null);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setQuery(current?.accountName || '');
        }
    }, selectedAccount);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce((accountName: string) => {
        const signal = createAbortSignal();
        if (accountName) {
            if (accountName.length < 3) {
                setBusy(false);
                setResponse({error: {
                    message: 'The search query is less than the minimum allowed, continue typing...',
                }});
                return;
            }

            apiDispatch(getAccountsByName({accountName}, {signal})).then(resp => {
                if (resp.payload.success) {
                    setResponse({
                        data: resp.payload.data
                            .filter(account => (accountId ? account.accountId !== accountId : true))
                            .slice(0, MAX_RESULTS),
                    });
                } else {
                    setResponse({error: {
                        message: 'Can\'t suggest parent accounts. Service is temporary unavailable.',
                    }});
                }
                setBusy(false);
            });
        } else {
            setBusy(false);
            setResponse(null);
            abort();
        }
    }, 1000), []);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        onSelect(undefined);
        setResponse(null);
    }, [onSelect]);

    const onItemSelect = useCallback((account: AccountShortInfo) => {
        onSelect(account);
        setQuery(account.accountName);
        setResponse(null);
    }, [onSelect]);

    return (
        <div style={{width}}>
            <Label htmlFor="select-account-autosuggest">{label}</Label>
            <Autosuggest
                id="select-account-autosuggest"
                value={query}
                onChange={onChangeQuery}
                disabled={disabled}
                placeholder={placeholder}
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <SearchAccountResult busy={busy} response={response}/>
            </Autosuggest>
        </div>
    );
};
